/** Languages the POS speaks. Vietnamese is the default; English second (FR-ORG-07). */
export const LANGUAGES = [
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'en', label: 'English' },
] as const;

export type Language = (typeof LANGUAGES)[number]['code'];

export const DEFAULT_LANGUAGE: Language = 'vi';

const STORAGE_KEY = 'pos.language';

/** Where the device's language choice is kept: the browser's localStorage unless an app (the native POS) sets another. */
export interface LanguageStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

let storage: LanguageStorage | undefined;

export function configureLanguageStorage(store: LanguageStorage) {
  storage = store;
}

const store = (): LanguageStorage => storage ?? localStorage;

export function isLanguage(value: unknown): value is Language {
  return LANGUAGES.some((l) => l.code === value);
}

/** The Intl locale for formatting text in a UI language (money and dates use the store's locale instead). */
export function localeOf(language: Language) {
  return language === 'vi' ? 'vi-VN' : 'en-GB';
}

/** This device's last chosen language; storage can be unavailable (private mode), so failures are ignored. */
export function savedLanguage(): Language | undefined {
  try {
    const value = store().getItem(STORAGE_KEY);
    return isLanguage(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function saveLanguage(language: Language) {
  try {
    store().setItem(STORAGE_KEY, language);
  } catch {
    // A preference only; the app works without it.
  }
}

/** Language for the login screen: this device's choice, else the browser's if supported, else Vietnamese. */
export function detectLanguage(): Language {
  const saved = savedLanguage();
  if (saved) return saved;
  // React Native has a navigator without a language.
  const browser = typeof navigator === 'undefined' ? undefined : navigator.language?.slice(0, 2).toLowerCase();
  return isLanguage(browser) ? browser : DEFAULT_LANGUAGE;
}
